import type { ReactNode } from 'react';

import { cn } from '../lib/cn';

interface ISectionPanelProps {
  title: string;
  note?: ReactNode;
  actions?: ReactNode;
  /** `danger`: the one panel on a screen whose actions cannot be taken back. */
  tone?: 'danger';
  id?: string;
  children: ReactNode;
  className?: string;
}

export const SectionPanel = ({
  title,
  note,
  actions,
  tone,
  id,
  children,
  className,
}: ISectionPanelProps) => (
  <section
    id={id}
    className={cn(
      'scroll-mt-page overflow-hidden rounded-panel border',
      tone === 'danger' ? 'border-danger-border' : 'border-border-sunken',
      className,
    )}
  >
    <header
      className={cn(
        'flex h-secthead items-center gap-inset border-b px-3',
        tone === 'danger' ? 'border-danger-border bg-danger-surface' : 'border-border-sunken',
      )}
    >
      <h3
        className={cn(
          'text-data font-semibold',
          tone === 'danger' ? 'text-danger-text' : 'text-primary',
        )}
      >
        {title}
      </h3>
      {note && <span className="truncate font-mono text-micro text-faint">{note}</span>}
      {actions && <div className="ml-auto flex shrink-0 items-center gap-2">{actions}</div>}
    </header>
    {children}
  </section>
);

/** Nothing here yet, inside a panel: what it is for and the next step, no second frame. */
export const PanelEmpty = ({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) => (
  <div className="flex items-center gap-6 px-card py-5">
    <div className="min-w-0 flex-1">
      <p className="text-data font-medium text-primary">{title}</p>
      {children && (
        <p className="mt-1 max-w-prose-line text-meta leading-18 text-muted">{children}</p>
      )}
    </div>
    {action && <div className="shrink-0">{action}</div>}
  </div>
);

/** A panel's closing line: a fact about the whole list, on the sunken band. */
export const PanelFoot = ({ children }: { children: ReactNode }) => (
  <p className="border-t border-border-sunken bg-field-muted px-card py-flat text-meta leading-18 text-muted">
    {children}
  </p>
);
